import { startSxpProxy } from '../utils/api'
import {
  ADMIN_PROJECT_ID,
  CHAT_PROJECT_ID,
  OPD_PROJECT_ID,
} from '../utils/constants'
import { emptyString } from '../features/Radiology/Constants/constants'
import {
  API_DUPLICATE_PATIENT_CHECK,
  API_PATH_HOSPITAL_CREATE,
  API_PATH_HOSPITAL_LIST,
  API_PATH_HOSPITAL_UPDATE,
  API_PATH_REFERRAL_CREATE,
  API_PATH_REFERRAL_FILTER,
  API_PATH_REFERRAL_LIST,
  API_PATH_REFERRAL_UPDATE,
  GET_HOSPITAL_LIST,
  REFERRAL_DETAILS_UPLOAD,
} from '../features/ReferralDetails/constants'
import HttpService from '../utils/httpService'
import { forms, formValues, intent } from '../features/administration/constants'
import { Practitioner, PractitionerRole } from 'fhir/r4'
import { FormDoctor } from '../features/administration/doctor/DoctorsForm'
import { AdminIntent } from '../features/lms/constants'
import {
  ERROR_UPLOADING_REFERRAL_DETAILS,
  UPLOAD_FAILED_MSG,
} from './constants'

type HospitalListResponse = {
  data: any
  success: boolean
}
const axiosClient = HttpService.getAxiosClient()
export const fetchHospitalList = async (): Promise<HospitalListResponse> => {
  try {
    const res: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${GET_HOSPITAL_LIST}`
    )
    if (res?.data?.referred_hospital_list?.length > 0) {
      return {
        data: res?.data?.referred_hospital_list,
        success: true,
      }
    } else {
      return {
        data: [],
        success: false,
      }
    }
  } catch (error) {
    // Handle error, optionally log or rethrow
    console.error('Error fetching hospital list:', error)
    return {
      data: [],
      success: false,
    }
  }
}
export const fetchHospitalListWithPaginations = async (
  page: number,
  pageSize: number
): Promise<HospitalListResponse> => {
  try {
    const res: any = await axiosClient.get(
      `${
        process.env.REACT_APP_API_GATEWAY_URL
      }${API_PATH_HOSPITAL_LIST}?limit=${pageSize}&offset=${page * pageSize}`
    )
    if (res?.data?.referred_hospital_list?.length > 0) {
      return {
        data: res?.data,
        success: true,
      }
    } else {
      return {
        data: null,
        success: false,
      }
    }
  } catch (error) {
    // Handle error, optionally log or rethrow
    console.error('Error fetching hospital list:', error)
    return {
      data: [],
      success: false,
    }
  }
}

export const createHospital = async (payload: any): Promise<void> => {
  await axiosClient.post(
    `${process.env.REACT_APP_API_GATEWAY_URL}${API_PATH_HOSPITAL_CREATE}`,
    { object: payload }
  )
}

export const updateHospital = async (
  id: string | number,
  payload: any
): Promise<void> => {
  await axiosClient.put(
    `${process.env.REACT_APP_API_GATEWAY_URL}${API_PATH_HOSPITAL_UPDATE(id)}`,
    { referred: payload }
  )
}

export const fetchReferrals = async (
  pageSize: number,
  page: number
): Promise<any> => {
  const res: any = await axiosClient.get(
    `${
      process.env.REACT_APP_API_GATEWAY_URL
    }${API_PATH_REFERRAL_LIST}?limit=${pageSize}&offset=${page * pageSize}`
  )
  return res?.data
}

export const filterReferrals = async (
  pageSize: number,
  page: number,
  filterKey: string,
  filterValue: string | string[],
  source?: string
): Promise<any> => {
  const normalizedFilterKey = filterKey?.toLowerCase()
  const isTimestampFilter = [
    'referral_date',
    'created_on',
    'updated_on',
  ].includes(normalizedFilterKey)
  const isArrayFilter = Array.isArray(filterValue)
  const dateValue =
    typeof filterValue === 'string' ? filterValue?.split('T')[0] : ''
  const fieldFilter = isArrayFilter
    ? {
        _in: filterValue,
      }
    : isTimestampFilter
    ? {
        _gte: `${dateValue}T00:00:00+00:00`,
        _lt: `${dateValue}T23:59:59.999+00:00`,
      }
    : {
        _ilike: `%${filterValue}%`,
      }

  const body = {
    limit: pageSize,
    offset: page * pageSize,
    filter: {
      ...(source
        ? {
            source: {
              _eq: source,
            },
          }
        : {}),
      [normalizedFilterKey]: fieldFilter,
    },
  }
  const res: any = await axiosClient.post(
    `${process.env.REACT_APP_API_GATEWAY_URL}${API_PATH_REFERRAL_FILTER}`,
    body
  )
  return res?.data
}

export const fetchDepartments = async (): Promise<any[]> => {
  const res: any = await startSxpProxy(
    ADMIN_PROJECT_ID,
    AdminIntent?.getSpecialty
  )
  const data = res?.data?.entry?.map((entry: any) => {
    const status = entry?.resource?.status || 'NA'
    const specialities = entry?.resource?.compose?.include[0]?.concept || []

    return {
      status,
      specialities: specialities?.map((concept: any) => ({
        code: concept?.code || 'NA',
        display: concept?.display || 'NA',
      })),
    }
  })
  return data?.[0]?.specialities || []
}

export const createReferral = async (payload: any): Promise<void> => {
  await axiosClient.post(
    `${process.env.REACT_APP_API_GATEWAY_URL}${API_PATH_REFERRAL_CREATE}`,
    { object: payload }
  )
}

export const uploadReferralDetails = async (file: File): Promise<any> => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const res: any = await axiosClient.post(
      `${process.env.REACT_APP_MEDUNITED_AWGMENT_GATEWAY}${REFERRAL_DETAILS_UPLOAD}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return {
      success: res?.data?.success ?? false,
      message: res?.data?.message ?? UPLOAD_FAILED_MSG,
    }
  } catch (err: any) {
    return {
      success: false,
      message:
        err?.response?.data?.message?.length > 0
          ? err?.response?.data?.message?.[0]
          : err?.response?.data?.message || ERROR_UPLOADING_REFERRAL_DETAILS,
    }
  }
}

export const updateReferral = async (
  id: string | number,
  payload: any
): Promise<void> => {
  await axiosClient.put(
    `${process.env.REACT_APP_API_GATEWAY_URL}${API_PATH_REFERRAL_UPDATE(id)}`,
    { referralDetails: payload }
  )
}

export const fetchPatientsByFhirIds = async (
  fhirIds: string
): Promise<any[]> => {
  const res = await startSxpProxy(OPD_PROJECT_ID, intent?.getPatientsByIdsApi, {
    ids: fhirIds,
  })
  return res?.data?.entry ?? []
}

export const fetchPatientsByMobile = async (
  mobile: string,
  selectedLocationId?: string | null
): Promise<any[]> => {
  const res = await startSxpProxy(CHAT_PROJECT_ID, 'getPatientsAPI', {
    pageSize: 100,
    query: 'phone',
    value: mobile,
    selectedLocationId,
  })
  return res?.data?.entry ?? []
}
export const formatDoctors = (
  roles: { resource: PractitionerRole }[],
  id?: string
) => {
  if (!id) {
    return 'NA'
  }

  const resType = roles?.find(
    (e) => e.resource?.practitioner?.reference?.split?.('/')?.[1] === id
  )
  const code =
    resType?.resource?.specialty?.[0]?.coding?.[0]?.code ?? emptyString
  const display =
    resType?.resource?.specialty?.[0]?.coding?.[0]?.display ?? emptyString
  return `${display}::${code}`
}
export const fetchDoctorsAsync = async (): Promise<FormDoctor[]> => {
  try {
    const res = await startSxpProxy(
      ADMIN_PROJECT_ID,
      intent?.getDoctorsAshaWorkers
    )
    const practitioners = res?.data?.entry?.filter(
      (e: { resource: { resourceType: string } }) =>
        e?.resource?.resourceType === forms?.practitioner
    )
    const roles: { resource: PractitionerRole }[] = res?.data?.entry?.filter(
      (e: { resource: { resourceType: string } }) =>
        e?.resource?.resourceType === forms?.practitionerRole
    )
    const data: FormDoctor[] = practitioners?.map(
      (e: { resource: Practitioner }) => {
        const role = roles?.find(
          (r) =>
            r?.resource?.practitioner?.reference?.split?.('/')?.[1] ===
            e?.resource.id
        )
        return {
          id: e?.resource?.id,
          firstName:
            e?.resource?.name?.[0]?.given?.join(emptyString) ?? emptyString,
          middleName: emptyString,
          lastName: e?.resource?.name?.[0]?.family ?? emptyString,
          salutation: e?.resource?.name?.[0]?.prefix?.[0] ?? emptyString,
          registeredOn:
            e?.resource?.identifier?.[1]?.period?.start ?? emptyString,
          dob: e?.resource?.birthDate,
          email:
            e?.resource?.telecom?.find(
              (contact: any) => contact?.system === formValues?.email
            )?.value || formValues?.na,
          phone:
            e?.resource?.telecom?.find(
              (contact: any) => contact?.system === formValues?.phone
            )?.value || formValues?.na,
          qualification:
            e?.resource?.qualification?.[0]?.code?.text || formValues?.na,
          gender: e?.resource?.gender || formValues?.na,
          regNumber: e?.resource?.qualification?.[0]?.identifier?.[0]?.value,
          speciality: formatDoctors(roles, e?.resource?.id) || formValues?.na,
          active: e?.resource?.active ?? true,
          operatingUnit: e?.resource?.address?.map((d: any) => {
            return d?.text
          })?.[0],
          healthService:
            role?.resource?.healthcareService?.map?.(
              (hs) => hs?.reference?.split?.('/')?.[1] ?? emptyString
            ) ?? [],
        }
      }
    )
    data.sort((a, b) => (a?.id < b?.id ? -1 : 1))
    return data
  } catch (err) {
    console.error('Error fetching doctors:', err)
    return []
  }
}
export const checkHospitalDuplicate = async (
  name: string,
  location: string,
  type?: string,
  excludeId?: string | number
): Promise<boolean> => {
  try {
    const params = new URLSearchParams({ name, location })
    if (type) params.append('type', type)
    if (excludeId) params.append('excludeId', excludeId.toString())
    const res: any = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${API_DUPLICATE_PATIENT_CHECK}?${params}`
    )
    return (res?.data?.referred_hospital_list?.length ?? 0) > 0
  } catch {
    return false
  }
}

export const exportReferrals = async (
  startDate: string,
  endDate: string,
  location = ''
) => {
  try {
    const response = await axiosClient.get(
      `${process.env.REACT_APP_API_GATEWAY_URL}${API_PATH_REFERRAL_LIST}?startDate=${startDate}&endDate=${endDate}&location=${location}`,
      { responseType: 'arraybuffer' }
    )

    // Create a blob from the response data
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    // Create a link element and trigger download
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `referrals_${startDate}_to_${endDate}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting referrals:', error)
    throw error
  }
}

export const exportReferralsByDateFilter = async (
  startDate: string,
  endDate?: string,
  location?: string | null
): Promise<any> => {
  try {
    const response = await axiosClient.get(
      `${
        process.env.REACT_APP_MEDUNITED_AWGMENT_GATEWAY
      }/api/export-referrals?startDate=${startDate}&endDate=${
        endDate ?? ''
      }&location=${location ?? ''}`,
      { responseType: 'arraybuffer' }
    )
    // Create a blob from the response data
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    // Create a link element and trigger download
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `referrals_${startDate}_to_${endDate || startDate}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting referrals by date filter:', error)
    throw error
  }
}

export const filterReferralsWithoutMobile = async (
  pageSize: number,
  page: number,
  filterKey: string,
  filterValue: string | string[],
  source?: string
): Promise<any> => {
  const body = {
    limit: pageSize,
    offset: page * pageSize,
    source: source ?? '',
    field: filterKey,
    filter: filterValue,
  }
  try {
    const response = await startSxpProxy(
      CHAT_PROJECT_ID,
      'getPatientReferralsByFilter',
      body
    )
    return {
      data: response?.data?.data,
      success: true,
    }
  } catch (error) {
    console.error('Error filtering referrals:', error)
    return {
      data: null,
      success: false,
    }
  }
}
